import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Archive,
  CheckCircle2,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
} from "lucide-react";
import {
  listBenefits,
  activateBenefit,
  archiveBenefit,
} from "../../../../../Services/adminBenefits";
import { Button } from "../../../../../ui/button";
import { Card, CardContent } from "../../../../../ui/card";
import { Badge } from "../../../../../ui/badge";
import { Skeleton } from "../../../../../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../../../ui/dropdown-menu";
import EmptyState from "../../../../../Components/admin/shared/EmptyState";
import ConfirmDialog from "../../../../../Components/admin/shared/ConfirmDialog";
import BenefitFormDialog from "./BenefitFormDialog";
import {
  benefitStatusBadgeVariant,
  extractApiError,
  humanEnum,
} from "./utils";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Drafts" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const MyBenefitsSection = ({ benefitsEnabled }) => {
  const { id: corporateId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const [editing, setEditing] = useState(null); // benefit or null
  const [showCreate, setShowCreate] = useState(false);

  const [activateTarget, setActivateTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    if (!corporateId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listBenefits(
        corporateId,
        statusFilter === "all" ? {} : { status: statusFilter }
      );
      setItems(data?.items || []);
    } catch (err) {
      setLoadError(extractApiError(err, "Failed to load benefits."));
    } finally {
      setLoading(false);
    }
  }, [corporateId, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleActivate = async () => {
    if (!activateTarget) return;
    setActionError(null);
    try {
      await activateBenefit(corporateId, activateTarget.id);
      setActivateTarget(null);
      load();
    } catch (err) {
      setActionError(extractApiError(err, "Failed to activate benefit."));
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    setActionError(null);
    try {
      await archiveBenefit(corporateId, archiveTarget.id);
      setArchiveTarget(null);
      load();
    } catch (err) {
      setActionError(extractApiError(err, "Failed to archive benefit."));
    }
  };

  const stats = useMemo(() => {
    const counts = { draft: 0, active: 0, archived: 0 };
    items.forEach((b) => {
      if (counts[b.status] !== undefined) counts[b.status] += 1;
    });
    return counts;
  }, [items]);

  return (
    <section className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">My benefits</h2>
          <p className="text-sm text-muted-foreground">
            Corporate-owned benefits — authored here, surfaced by ARIS once
            activated.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New benefit
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      {loadError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {loadError}
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="pt-4 divide-y">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="py-3 first:pt-0 last:pb-0">
                <Skeleton className="h-5 w-1/3 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="No benefits yet"
          description="Author your first benefit. It'll land in draft so you can review before ARIS may surface it."
          action={{
            label: "Create a benefit",
            onClick: () => setShowCreate(true),
          }}
        />
      ) : (
        <Card>
          <CardContent className="pt-4 divide-y">
            <div className="pb-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>Active: {stats.active}</span>
              <span>Draft: {stats.draft}</span>
              <span>Archived: {stats.archived}</span>
            </div>
            {items.map((b) => (
              <div
                key={b.id}
                className="py-4 first:pt-0 last:pb-0 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold">{b.name}</h3>
                    <Badge variant={benefitStatusBadgeVariant(b.status)}>
                      {humanEnum(b.status)}
                    </Badge>
                    {b.crisis_only && (
                      <Badge variant="warning">Crisis only</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {b.provider} · {humanEnum(b.category)} · Priority {b.priority} ·{" "}
                    Cooldown {b.cooldown_days}d
                  </p>
                  {Array.isArray(b.signals) && b.signals.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {b.signals.map((s) => (
                        <span
                          key={s.id}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${
                            s.is_active === false
                              ? "bg-muted text-muted-foreground line-through"
                              : "bg-primary/10 text-primary"
                          }`}
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {b.helps_with && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {b.helps_with}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(b)}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  {b.status !== "active" && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setActivateTarget(b)}
                      disabled={!benefitsEnabled}
                      title={
                        benefitsEnabled
                          ? undefined
                          : "Enable benefits in the header before activating."
                      }
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Activate
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {b.status === "active" && (
                        <DropdownMenuItem
                          onClick={() => setArchiveTarget(b)}
                          className="text-destructive"
                        >
                          <Archive className="h-4 w-4 mr-2" /> Archive
                        </DropdownMenuItem>
                      )}
                      {b.status === "archived" && (
                        <DropdownMenuItem
                          onClick={() => setActivateTarget(b)}
                          disabled={!benefitsEnabled}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" /> Re-activate
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <BenefitFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        benefit={null}
        onSaved={() => load()}
      />

      <BenefitFormDialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        benefit={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <ConfirmDialog
        open={!!activateTarget}
        onOpenChange={(open) => !open && setActivateTarget(null)}
        title="Activate this benefit?"
        description={
          activateTarget
            ? `Once active, ARIS may surface "${activateTarget.name}" when signals match.`
            : ""
        }
        confirmLabel="Activate"
        onConfirm={handleActivate}
      />

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
        title="Archive this benefit?"
        description={
          archiveTarget
            ? `"${archiveTarget.name}" will no longer be surfaced by ARIS. You can re-activate it later.`
            : ""
        }
        confirmLabel="Archive"
        destructive
        onConfirm={handleArchive}
      />
    </section>
  );
};

export default MyBenefitsSection;
